import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import Utilities from "../../dependencies/custom/react-utilities";
import domainSettings from "../../dependencies/custom/settings";

export const usePickupLists = () => {
  const navigate = useNavigate();
  const [pickupLists, setPickupLists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const fetchPickupLists = useCallback(async () => {
    setLoading(true);
    try {
      let url = domainSettings.urls.bot.pickupLists;
      const params = [];

      if (filterStatus) params.push(`status=${filterStatus}`);
      if (params.length > 0) url += `?${params.join("&")}`;

      const response = await Utilities.fetchWithAuth(
        "GET",
        url,
        null,
        navigate,
      );

      if (response.ok) {
        setPickupLists(response.data.results || response.data || []);
      } else {
        Utilities.showError(response, "Failed to fetch pickup lists");
      }
    } catch (error) {
      message.error("An error occurred while fetching pickup lists");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [navigate, filterStatus]);

  const createPickupList = useCallback(
    async (clearanceAssignmentId, pickupDate, notes) => {
      try {
        const payload = {
          clearance_assignment: clearanceAssignmentId,
          pickup_date: pickupDate,
          notes: notes,
        };

        const response = await Utilities.fetchWithAuth(
          "POST",
          domainSettings.urls.bot.pickupLists,
          payload,
          navigate,
        );

        if (response.ok) {
          message.success("Pickup list created successfully");
          fetchPickupLists();
          return response.data;
        } else {
          Utilities.showError(response, "Failed to create pickup list");
          return null;
        }
      } catch (error) {
        message.error("An error occurred while creating pickup list");
        console.error(error);
        return null;
      }
    },
    [navigate, fetchPickupLists],
  );

  const markPrepared = useCallback(
    async (pickupListId, notes) => {
      try {
        const url = domainSettings.urls.bot.markPrepared.replace(
          ":id",
          pickupListId,
        );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          { notes },
          navigate,
        );

        if (response.ok) {
          message.success("Pickup list marked as prepared");
          fetchPickupLists();
          return true;
        } else {
          Utilities.showError(response, "Failed to mark as prepared");
          return false;
        }
      } catch (error) {
        message.error("An error occurred");
        console.error(error);
        return false;
      }
    },
    [navigate, fetchPickupLists],
  );

  const notifyParent = useCallback(
    async (pickupListId) => {
      try {
        const url = domainSettings.urls.bot.notifyParent.replace(
          ":id",
          pickupListId,
        );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          {},
          navigate,
        );

        if (response.ok) {
          message.success("Parent notified successfully");
          fetchPickupLists();
          return true;
        } else {
          Utilities.showError(response, "Failed to notify parent");
          return false;
        }
      } catch (error) {
        message.error("An error occurred while notifying parent");
        console.error(error);
        return false;
      }
    },
    [navigate, fetchPickupLists],
  );

  const fetchPickupListDetails = useCallback(
    async (pickupListId) => {
      try {
        const url = domainSettings.urls.bot.pickupListDetail.replace(
          ":id",
          pickupListId,
        );
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          return response.data;
        } else {
          Utilities.showError(response, "Failed to fetch pickup list details");
          return null;
        }
      } catch (error) {
        message.error("An error occurred while fetching details");
        console.error(error);
        return null;
      }
    },
    [navigate],
  );

  const filteredPickupLists = pickupLists.filter((list) => {
    const searchLower = searchText.toLowerCase();
    return (
      list.list_number?.toLowerCase().includes(searchLower) ||
      list.student_name?.toLowerCase().includes(searchLower) ||
      list.student_spc?.toLowerCase().includes(searchLower) ||
      list.botpc?.toLowerCase().includes(searchLower)
    );
  });

  return {
    pickupLists,
    filteredPickupLists,
    loading,
    searchText,
    setSearchText,
    filterStatus,
    setFilterStatus,
    fetchPickupLists,
    createPickupList,
    markPrepared,
    notifyParent,
    fetchPickupListDetails,
  };
};
